import {
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
  untracked,
} from '@angular/core';
import {
  CompositeFilterDescriptor,
  filterBy,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import { FilterService } from '@progress/kendo-angular-grid';
import { SortFilterItemsService } from '../../sort-filter-items.service';

import {
  patchToUiCollectionFilter,
  fetchSimpleCompositeFilterFromUiCollectionFilter,
} from '../../collection-filter-utils';
import { GridCheckboxFilterMenuUIService } from './grid-checkbox-filter-menu.ui-service';

export interface CheckBoxFilterSearchOption {
  text: string;
  value: string;
}

/** The current input values of a checkbox filter menu, read through one accessor. */
export interface CheckBoxFilterMenuOptions {
  height: number;
  isLoadingFilterData: boolean;
  isPrimitive: boolean;
  textField: string;
  valueField: string;
  hintField: string;
  currentFilter: CompositeFilterDescriptor;
  filterService: FilterService;
  field: string;
  operator: string;
  compositeLogic: 'or' | 'and';
  collectionField: string;
  collectionRef: string;
  collectionItemField: string;
  collectionOperator: 'any' | 'all';
  isSingleSelection: boolean;
  alwaysShowSelectedItems: boolean;
  keepExtraFilterValues: boolean;
  showFilter: boolean;
  showFilterOperators: boolean;
  addSelectAllOption: boolean;
  addBlankOption: boolean;
  customBlankOptionText: string;
  isGuidValue: boolean;
}

/** One rendered checkbox row: everything the template needs, precomputed. */
export interface CheckBoxFilterMenuRow {
  item: unknown;
  id: unknown;
  text: unknown;
  hint: unknown;
  selected: boolean;
}

/**
 * Shared state and behavior of the two checkbox filter menus (WI 35564).
 *
 * `grid-checkbox-filter-menu` (decorator inputs, the 290+ existing call sites) and
 * `grid-checkbox-filter-menu-signal` (signal inputs) differ only in how their inputs
 * arrive, so each subclass supplies `opts()`, `rawItems` and `searchOperator`, and both
 * render the one shared template from the `view` computed. Every template read is a
 * signal, so the menus are OnPush-safe.
 *
 * Parity note: `visibleItems` reads the selection untracked, as the original only
 * re-filtered the visible list on data, search text and search operator changes, never
 * on a checkbox toggle (so an unchecked item stays visible while a search is active).
 */
export abstract class CheckBoxFilterMenuBase {
  public static blankFilterText = 'Blank';
  public static blankFilterValue = 'BlankValue'; // TODO: prefix with something $$
  public static notBlankFilterText = 'Not Blank';
  public static notBlankFilterValue = 'NotBlankValue'; // TODO: prefix with something $$

  public readonly dataItemSearchOptions: CheckBoxFilterSearchOption[] = [
    { text: 'Contains', value: 'contains' },
    { text: 'Does not contain', value: 'doesnotcontain' },
    { text: 'Starts with', value: 'startswith' },
    { text: 'Ends wit', value: 'endswith' },
    { text: 'Is equal to', value: 'isequalto' },
    { text: 'Is not equal to', value: 'isnotequalto' },
  ];

  protected readonly gridCheckboxFilterMenuUIService = inject(
    GridCheckboxFilterMenuUIService
  );
  protected readonly sortService = inject(SortFilterItemsService);

  /** selected item's value fields */
  protected readonly selected = signal<unknown[]>([]);
  protected readonly searchText = signal('');

  /** Current input values; reading it inside a computed tracks the signal-backed ones. */
  protected abstract opts(): CheckBoxFilterMenuOptions;
  /** The `data` input as received (undefined until the host binds it). */
  protected abstract readonly rawItems: Signal<unknown[] | undefined>;
  protected abstract readonly searchOperator: WritableSignal<string>;

  /** The checkbox list source (mirrors the original `get data()`). */
  protected readonly menuData = computed<unknown[] | undefined>(() => {
    const sorted = this.sortedItems();
    let d = sorted ? [...sorted] : undefined;
    if (this.opts().addBlankOption && d) d = this.insertBlankOption(d);
    return d;
  });

  /** Items shown in the checkbox list after the search box filter. */
  protected readonly visibleItems = computed<unknown[]>(() => {
    const o = this.opts();
    const searchFilter = {
      logic: 'or',
      filters: [
        {
          operator: this.searchOperator(), // operator for search
          field: o.textField,
          value: this.searchText(),
        },
      ],
    } as CompositeFilterDescriptor;
    if (o.alwaysShowSelectedItems) {
      const selected = untracked(this.selected) || [];
      searchFilter.filters.push(
        ...selected.map(
          (v) =>
            ({
              operator: 'eq',
              field: o.valueField,
              value: v,
            }) as FilterDescriptor
        )
      );
    }
    return filterBy(this.menuData() || [], searchFilter);
  });

  protected readonly rows = computed<CheckBoxFilterMenuRow[]>(() =>
    this.visibleItems().map((item) => ({
      item,
      id: this.valueAccessor(item),
      text: this.textAccessor(item),
      hint: this.hintAccessor(item),
      selected: this.isItemSelected(item),
    }))
  );

  /** Everything the shared template reads, as one memoized signal. */
  protected readonly view = computed(() => {
    const o = this.opts();
    const data = this.menuData();
    return {
      loading: o.isLoadingFilterData,
      onlyBlank: data?.length === 0,
      blankText: o.customBlankOptionText || 'Blank',
      height: o.height,
      showFilter: o.showFilter,
      showFilterOperators: o.showFilterOperators,
      addSelectAllOption: o.addSelectAllOption,
      allSelected: o.addSelectAllOption && this.areAllItemsSelected(),
      searchText: this.searchText(),
      searchOperator: this.searchOperator(),
      rows: this.rows(),
    };
  });

  private didSeedFromFilter = false;

  /** Incoming items, sorted the way the original `set data()` sorted them. */
  private readonly sortedItems = computed(() => {
    const items = this.rawItems();
    if (items === undefined) return undefined;
    const o = this.opts();
    return !o.isLoadingFilterData && !o.isSingleSelection
      ? this.sortData(items)
      : items;
  });

  public insertBlankOption(data: unknown[]): unknown[] {
    // the list of options should only include a 'Blank' option when the column contains
    // blank rows; the blank rows themselves are replaced by that one option
    if (!this.containsBlankRows(data)) return data;
    data = this.removeBlankRow(data);
    const o = this.opts();
    const text =
      o.customBlankOptionText || CheckBoxFilterMenuBase.blankFilterText;
    let blankItem: unknown = text;
    if (!o.isPrimitive) {
      blankItem = {
        [o.valueField]: CheckBoxFilterMenuBase.blankFilterValue,
        [o.textField]: text,
      };
    }
    data.unshift(blankItem);
    return data;
  }

  public processFiltering() {
    const o = this.opts();
    const values = untracked(this.selected);
    const selectedValues = o.keepExtraFilterValues
      ? values
      : this.filterValidSelections(values);

    if (!selectedValues || !selectedValues.length) {
      o.filterService.filter({ logic: 'or', filters: [] });
      return;
    }

    const blankText =
      o.customBlankOptionText || CheckBoxFilterMenuBase.blankFilterText;
    const compositeFilter = {
      logic: o.compositeLogic,
      filters: selectedValues.map((v) => {
        // regular filter with select blank or not blank or values
        if (o.isPrimitive && o.addBlankOption && v === blankText)
          return { operator: 'isnull', field: o.field };
        switch (v) {
          case CheckBoxFilterMenuBase.blankFilterValue:
            return { operator: 'isnull', field: o.field };
          case CheckBoxFilterMenuBase.notBlankFilterValue:
            return { operator: 'isnotnull', field: o.field };
          default:
            return {
              operator: o.operator,
              field: o.field,
              value: v,
              ignoreCase: !o.isGuidValue,
              ignoreValueCase: !o.isGuidValue,
              skipQuotes: o.isGuidValue,
            };
        }
      }),
    } as CompositeFilterDescriptor;

    if (o.collectionField) {
      const collectionBlankNotBlankOverride = true;
      o.filterService.filter(
        patchToUiCollectionFilter(
          compositeFilter,
          o.field,
          o.collectionField,
          o.collectionItemField,
          o.collectionOperator,
          o.collectionRef,
          collectionBlankNotBlankOverride
        )
      );
      return;
    }

    o.filterService.filter(compositeFilter);
  }

  public containsBlankRows(data: unknown[]): boolean {
    const o = this.opts();
    return this.gridCheckboxFilterMenuUIService.containsBlankRows(
      data,
      o.isPrimitive,
      o.valueField
    );
  }

  public removeBlankRow(data: unknown[]): unknown[] {
    const o = this.opts();
    return data.filter(
      (item) =>
        !this.gridCheckboxFilterMenuUIService.isItemBlank(
          item,
          o.isPrimitive,
          o.valueField
        )
    );
  }

  public isBlank(item: unknown) {
    const o = this.opts();
    return o.isPrimitive
      ? item === CheckBoxFilterMenuBase.blankFilterText
      : item[o.valueField] === CheckBoxFilterMenuBase.blankFilterValue;
  }

  public isNotBlank(item: unknown) {
    const o = this.opts();
    return o.isPrimitive
      ? item === CheckBoxFilterMenuBase.notBlankFilterText
      : item[o.valueField] === CheckBoxFilterMenuBase.notBlankFilterValue;
  }

  public textAccessor = (dataItem: unknown) =>
    this.opts().isPrimitive ? dataItem : dataItem[this.opts().textField];

  public valueAccessor = (dataItem: unknown) =>
    this.opts().isPrimitive ? dataItem : dataItem[this.opts().valueField];

  public hintAccessor = (dataItem: unknown) => {
    const { isPrimitive, hintField } = this.opts();
    return isPrimitive || !hintField
      ? null
      : ((dataItem as Record<string, unknown> | null)?.[hintField] ?? null);
  };

  public isItemSelected(item: unknown): boolean {
    return this.selected().some(
      (x) =>
        x === this.valueAccessor(item) ||
        (x === CheckBoxFilterMenuBase.blankFilterValue &&
          item === CheckBoxFilterMenuBase.blankFilterText)
    );
  }

  public areAllItemsSelected(): boolean {
    const data = this.menuData();
    if (!data) return true;
    const selected = this.selected();
    return data.every((dataItem) =>
      selected.includes(this.valueAccessor(dataItem))
    );
  }

  /** when single item was checked */
  public onSelectionChange(checked: boolean, dataItem: unknown) {
    // NOTE: we try to avoid to clear extra filter values (set from outside)
    const value = this.valueAccessor(dataItem);
    if (checked) {
      // clear other if in 'single' mode
      if (this.opts().isSingleSelection) this.unselectAll();
      this.selected.update((v) => [...v, value]);
    } else {
      this.selected.update((v) => v.filter((x) => x !== value));
    }
    this.processFiltering();
  }

  /** when select-all changed: (un)selects the VISIBLE items only */
  public onSelectAllChange(checked: boolean) {
    const dataItemsToConsider = this.visibleItems();
    // NOTE: we try to avoid to clear extra filter values (set from outside)
    if (checked) {
      const current = untracked(this.selected);
      const toAdd = dataItemsToConsider
        .map((dataItem) => this.valueAccessor(dataItem))
        .filter((v, i, all) => !current.includes(v) && all.indexOf(v) === i);
      if (toAdd.length) this.selected.update((v) => [...v, ...toAdd]);
    } else {
      this.unselectAll(dataItemsToConsider);
    }
    this.processFiltering();
  }

  public dataItemSearchFilterOperatorChange(
    searchOption: CheckBoxFilterSearchOption
  ): void {
    // NOTE: the dropdown's selectionChange fires before its valueChange, so set it here
    this.searchOperator.set(searchOption.value);
  }

  public dataItemFilterTextChange(searchText: unknown) {
    this.searchText.set(searchText as string);
  }

  public parseValueFromFilter(filter: CompositeFilterDescriptor): unknown[] {
    // NOTE: originally we used an extra property, now as we have a parser for our complex filter, we use that (it's more general)
    const valuesCompositeFilter =
      fetchSimpleCompositeFilterFromUiCollectionFilter(
        filter,
        this.opts().field
      );

    return valuesCompositeFilter.filters?.map((f: FilterDescriptor) => {
      if (f.operator === 'isnull')
        return CheckBoxFilterMenuBase.blankFilterValue;
      if (f.operator === 'isnotnull')
        return CheckBoxFilterMenuBase.notBlankFilterValue;
      return f.value;
    });
  }

  public sortData(dataToBeSorted: unknown[]): unknown[] {
    const returnValue = [...dataToBeSorted];
    const o = this.opts();
    if (o.isPrimitive) {
      this.sortService.sortArray(returnValue);
    } else {
      this.sortService.sortFilteredItems(returnValue, o.textField);
    }
    return returnValue;
  }

  /** Parses the assigned filter into the selection, once (later re-emits must not re-concat). */
  protected seedFromFilter(filter: CompositeFilterDescriptor): void {
    if (!filter || this.didSeedFromFilter) return;
    this.didSeedFromFilter = true;
    const parsed = this.parseValueFromFilter(filter) ?? [];
    this.selected.update((v) => (v || []).concat(...parsed));
  }

  protected filterValidSelections(values: unknown[]): unknown[] {
    const data = this.menuData() || [];
    return (values || []).filter((v) =>
      data.some((dataItem) => this.valueAccessor(dataItem) === v)
    );
  }

  protected unselectAll(dataItemsToUnselect?: unknown[]) {
    // remove all items (that are in the data[])
    const dataItems = dataItemsToUnselect || this.menuData() || [];
    const toRemove = new Set(dataItems.map((d) => this.valueAccessor(d)));
    this.selected.update((v) => v.filter((x) => !toRemove.has(x)));
  }
}
